import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Eye, EyeOff, Lock } from "lucide-react";
import jobLogo from "../images/Logo.svg";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const handleResetPassword = async (values, { setSubmitting }) => {
    try {
      setError("");
      setMessage("");

      const response = await axios.post(`${REACT_APP_API_URL}/api/auth/reset-password`, {
        token,
        password: values.password,
      });

      if (response.data?.message) {
        setMessage(response.data.message);
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setMessage("Password has been reset. Redirecting...");
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Reset failed. Try again.");
    }

    setSubmitting(false);
  };

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token.");
    }
  }, [token]);

  return (
    <div
      className="min-h-screen w-full font-['Inter',sans-serif] flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
        />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
        />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
        />
      </div>

      {/* Card */}
      <div className="relative w-full !max-w-lg bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 z-10"
        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}
      >
        {/* Branding */}
        <div className="flex flex-col items-center mb-8">
          <img src={jobLogo} alt="Logo" className="h-16 mb-4 cursor-pointer" onClick={() => navigate("/")} />
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Reset Password</h1>
          <p className="text-gray-500 text-sm mt-1 text-center">Enter your new password below</p>
        </div>

        {/* Success Message */}
        {message && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4 text-center">
            {message}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4 text-center">
            {error}
          </div>
        )}

        {!error && (
          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={validationSchema}
            onSubmit={handleResetPassword}
          >
            {({ isSubmitting }) => (
              <Form>
                {/* New Password */}
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Field
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter new password"
                      className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <ErrorMessage name="password" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
                </div>

                {/* Confirm Password */}
                <div className="mb-6">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Field
                      type={showConfirm ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Re-enter password"
                      className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 text-white font-semibold text-sm rounded-xl border-none cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  <Lock size={18} />
                  {isSubmitting ? "Resetting..." : "Reset Password"}
                </button>
              </Form>
            )}
          </Formik>
        )}

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <span className="text-sm text-gray-500">
            Back to{" "}
            <span
              className="text-blue-600 font-semibold cursor-pointer hover:text-blue-700 transition-colors"
              onClick={() => navigate("/login")}
            >
              Sign In
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
